/**
 * Extract a human-readable string from an axios error response.
 *
 * FastAPI returns two different shapes for error bodies:
 *   - 4xx from raise HTTPException:    { detail: "some string" }
 *   - 422 from Pydantic validation:    { detail: [ { type, loc, msg, input, ctx, url }, ... ] }
 *
 * Passing the validation array straight to `toast.error(...)` crashes React
 * with "Objects are not valid as a React child". This helper collapses either
 * shape into a readable string so call sites can do:
 *     toast.error(getApiErrorMessage(error, 'Fallback message'))
 */
export function getApiErrorMessage(error, fallback = 'Something went wrong') {
  const data = error?.response?.data;
  const detail = data?.detail;

  // Some middleware (e.g. entitlements) returns { message, error_code } instead of { detail }
  if (!detail) {
    if (typeof data?.message === 'string') return data.message;
    return fallback;
  }
  if (typeof detail === 'string') return detail;

  if (Array.isArray(detail)) {
    // Pydantic validation errors: show the field path + message for each.
    return detail
      .map((e) => {
        const field = Array.isArray(e?.loc) ? e.loc.filter((p) => p !== 'body').join('.') : '';
        const msg = e?.msg || 'invalid';
        return field ? `${field}: ${msg}` : msg;
      })
      .join('; ');
  }

  // detail is an object (e.g. entitlements: { message, error_code })
  if (typeof detail === 'object' && detail !== null) {
    if (typeof detail.message === 'string') return detail.message;
    if (typeof detail.msg === 'string') return detail.msg;
  }

  // Unknown shape -- last-resort stringify
  try {
    return JSON.stringify(detail);
  } catch {
    return fallback;
  }
}
